import React from 'react';

import ModelTypeConstants, {SEQUENCE_SUBTYPES} from 'constants/ModelTypeConstants';
import {StorableSummary} from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {GlobalSearchResponse} from 'search/GlobalSearchDAO';
import {
  FilterKey,
  keyToEmptyFilter,
  MultiValueFilter,
  Operator,
  GLOBAL_SEARCH_URL,
  SearchContext,
  SortKey,
} from 'search/SearchConstants';
import ItemSearchInput, {Item, ItemSearchInputProps} from 'ui-library/attach-dropdown/ItemSearchInput';
import {searchPost} from 'ui-library/inputs/ChipSearchInput';
import {getIconIdForSearchResult} from 'ui-library/inputs/ItemOptionContent';
import APIUtil from 'util/APIUtil';

import {BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';
import {ItemSearchMultiValueFilterForm} from './ItemSearchFilterForm';

interface AnyLinkSchemaFieldFilterFormProps extends BaseFilterFormProps {
  filter: MultiValueFilter;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: MultiValueFilter) => void;
  onEnter?: () => void;
  onRemove?: () => void;
  operators: Array<Operator>;
}

// keep this as a distinct subset of the list on benchling/search/interface/filters/schema_tag_filter.py L767
const SUPPORTED_MODEL_TYPES = [
  ...SEQUENCE_SUBTYPES,
  ModelTypeConstants.BASIC_FOLDER_ITEM,
  ModelTypeConstants.MIXTURE,
  ModelTypeConstants.MOLECULE,
  ModelTypeConstants.PROTEIN,
  ModelTypeConstants.CONTAINER,
  ModelTypeConstants.PLATE,
  ModelTypeConstants.BOX,
  ModelTypeConstants.LOCATION,
];

function AnySchemaFieldItemSearchInput(
  props: Omit<ItemSearchInputProps<BioentitySummary | StorableSummary>, 'itemKey' | 'searchUrl'> & {
    context?: SearchContext;
  }
): JSX.Element {
  const filters = [
    {
      ...keyToEmptyFilter(FilterKey.TYPES),
      value: SUPPORTED_MODEL_TYPES,
    },
  ];
  return (
    <ItemSearchInput
      {...props}
      doSearch={searchPost}
      fetchParams={{
        filters,
        offset: 0,
        sorts: [{key: SortKey.SORT_RELEVANCE_KEY, reverse: false}],
        context: props.context,
      }}
      itemKey="fileSummariesById"
      getOptionDatasFromResponse={(searchResponse: GlobalSearchResponse) => {
        const idsToIgnore = props.hideSelectedResults ? props.selectedItems.map((item) => item.id) : [];
        const keys: Array<keyof GlobalSearchResponse> = ['fileSummariesById', 'storableSummariesById'];
        const filteredItems: Array<BioentitySummary | StorableSummary> = keys
          .reduce((acc, key) => [...acc, ...Object.values(searchResponse[key]!)], [] as Array<Item>)
          .filter(
            (item: BioentitySummary | StorableSummary) => item.id != null && !idsToIgnore.includes(item.id)
          );
        return filteredItems.map((item) => {
          const value = {
            item,
            item_id: item.id,
            name: item.name,
            type: APIUtil.apiIdToType(item.id),
          };
          return {
            name: item.name,
            icon: getIconIdForSearchResult(value),
            value,
          };
        });
      }}
      placeholder="Search for items"
      searchURL={GLOBAL_SEARCH_URL}
    />
  );
}

export default function AnyLinkSchemaFieldFilterForm(props: AnyLinkSchemaFieldFilterFormProps): JSX.Element {
  return (
    <ItemSearchMultiValueFilterForm
      {...props}
      itemSearchInputClass={AnySchemaFieldItemSearchInput}
      itemSearchInputExtraProps={{shouldAllowMultiple: true}}
    />
  );
}
